import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Check, ChevronsUpDown, ShieldAlert, Layers } from 'lucide-react';
import { useAuth } from '../lib/auth';
import { cn } from '../lib/cn';

/**
 * Which hat you are wearing, and how to change it.
 *
 * Every estate is listed under the client that owns it, because "which Acme?"
 * is a question a contractor working for three customers will actually have.
 * An estate reached by owning the client is marked as such rather than shown
 * as though it were an ordinary membership — how you got in is worth knowing
 * before you change something.
 */
export default function ScopeSwitcher() {
  const { scope, scopes, tenant, org, switchScope, isImpersonating } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  // A support session is left through its own banner, not by quietly changing
  // hats — the audit trail needs a close as well as an open.
  if (isImpersonating) return null;

  const nothingToSwitchTo =
    !scopes.platform && scopes.orgs.length === 0 && scopes.tenants.length <= 1;
  if (nothingToSwitchTo) return null;

  const label =
    scope?.kind === 'platform' ? 'Platform'
    : scope?.kind === 'org' ? (org?.name ?? 'Client')
    : (tenant?.name ?? 'Select an estate');

  const sublabel =
    scope?.kind === 'platform' ? 'All clients'
    : scope?.kind === 'org' ? 'Client console'
    : (org?.name ?? '');

  const go = async (target: Parameters<typeof switchScope>[0], to: string) => {
    setBusy(true);
    try {
      await switchScope(target);
      setOpen(false);
      navigate(to);
    } finally {
      setBusy(false);
    }
  };

  const byOrg = new Map<string, typeof scopes.tenants>();
  for (const t of scopes.tenants) {
    if (!byOrg.has(t.org_id)) byOrg.set(t.org_id, []);
    byOrg.get(t.org_id)!.push(t);
  }

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        disabled={busy}
        aria-expanded={open}
        aria-label="Switch scope"
        className="w-full flex items-center gap-2.5 rounded-lg px-3 py-2 hover:bg-[rgb(var(--surface-raised))] disabled:opacity-60"
      >
        <span className={cn('h-7 w-7 rounded-lg grid place-items-center shrink-0 text-white',
          scope?.kind === 'platform' ? 'bg-purple-600' : scope?.kind === 'org' ? 'bg-amber-600' : 'bg-brand-600')}>
          {scope?.kind === 'platform' ? <ShieldAlert size={15} />
            : scope?.kind === 'org' ? <Building2 size={15} /> : <Layers size={15} />}
        </span>
        <span className="min-w-0 flex-1 text-start">
          <span className="block text-sm text-app truncate">{label}</span>
          {sublabel && <span className="block text-[11px] text-muted truncate">{sublabel}</span>}
        </span>
        <ChevronsUpDown size={14} className="text-muted shrink-0" />
      </button>

      {open && (
        <div className="absolute bottom-full mb-2 start-0 end-0 surface app-border border rounded-lg shadow-lg p-1.5 max-h-[70vh] overflow-y-auto">
          {scopes.platform && (
            <>
              <p className="px-2 pt-1 pb-1.5 text-[11px] font-medium text-muted uppercase tracking-wide">Platform</p>
              <button
                onClick={() => void go({ kind: 'platform' }, '/platform')}
                className="w-full flex items-center gap-2 rounded-md px-2 py-2 text-sm hover:bg-[rgb(var(--surface-raised))] text-start"
              >
                <ShieldAlert size={15} className="text-purple-600 shrink-0" />
                <span className="flex-1 truncate">
                  Platform console
                  <span className="block text-[11px] text-muted capitalize">{scopes.platform.role}</span>
                </span>
                {scope?.kind === 'platform' && <Check size={14} className="text-brand-600" />}
              </button>
            </>
          )}

          {scopes.orgs.map((o) => (
            <div key={o.id}>
              <p className="px-2 pt-2 pb-1 text-[11px] font-medium text-muted uppercase tracking-wide truncate">
                {o.name}
              </p>
              <button
                onClick={() => void go({ kind: 'org', orgId: o.id }, '/client')}
                className="w-full flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-[rgb(var(--surface-raised))] text-start"
              >
                <Building2 size={14} className="text-amber-600 shrink-0" />
                <span className="flex-1 truncate">Client console</span>
                {scope?.kind === 'org' && scope.orgId === o.id && <Check size={14} className="text-brand-600" />}
              </button>
              {(byOrg.get(o.id) ?? []).map((t) => (
                <button
                  key={t.id}
                  onClick={() => void go({ kind: 'tenant', tenantId: t.id }, '/')}
                  className="w-full flex items-center gap-2 rounded-md ps-6 pe-2 py-1.5 text-sm hover:bg-[rgb(var(--surface-raised))] text-start"
                >
                  <span className="flex-1 truncate">
                    {t.name}
                    <span className="block text-[11px] text-muted">
                      {t.via === 'org' ? 'via client' : t.role}
                    </span>
                  </span>
                  {scope?.tenantId === t.id && <Check size={14} className="text-brand-600 shrink-0" />}
                </button>
              ))}
            </div>
          ))}

          {/* Estates the person is a direct member of, whose client they do not
              administer — the contractor case. */}
          {scopes.tenants.filter((t) => !scopes.orgs.some((o) => o.id === t.org_id)).length > 0 && (
            <>
              <p className="px-2 pt-2 pb-1 text-[11px] font-medium text-muted uppercase tracking-wide">Estates</p>
              {scopes.tenants
                .filter((t) => !scopes.orgs.some((o) => o.id === t.org_id))
                .map((t) => (
                  <button
                    key={t.id}
                    onClick={() => void go({ kind: 'tenant', tenantId: t.id }, '/')}
                    className="w-full flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-[rgb(var(--surface-raised))] text-start"
                  >
                    <Layers size={14} className="text-brand-600 shrink-0" />
                    <span className="flex-1 truncate">
                      {t.name}
                      <span className="block text-[11px] text-muted">{t.org_name} · {t.role}</span>
                    </span>
                    {scope?.tenantId === t.id && <Check size={14} className="text-brand-600 shrink-0" />}
                  </button>
                ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}
